import React, { useCallback, useEffect } from "react";
import { Accordion, Button, Card, Col, Row } from "react-bootstrap";
import { appendScript } from "../../helper/appendScript";
import { removeScript } from "../../helper/removeScript";
import { getPatientReport } from "../../services/getFunctions";
import Footer from "../Footer";
import NavigationBar from "../navigation/NavigationBar";
import SidebarClinician from "../sidebars/SidebarClinician";
import SidebarPatient from "../sidebars/SidebarPatient";
import { useAlertHelpers } from "../../helper/Hooks/useAlertHelpers";
import { useLocation, useNavigate } from "react-router";
import { LocationState } from "../../types/routeTypes";

// icons
import { MdLocalPrintshop, MdViewList } from "react-icons/md";
import { usePartialState } from "../../helper/Hooks/usePartialState";
import { toErrorType } from "../../helper/alertBoxes";
import { getGermanTodayDisplay } from "../../helper/dateHelpers";

/**
 * This file is not used and the css is maybe broken.
 * If this file will be used in the future please look at
 * the Clinician Report and recreate the css
 * The css classes are also often not used and are rendundant (e.g. .page-wrapper-new, .content-wrapper)
 */

interface PatientReportState {
  user_type: string;
  clinic_id: string;
  clinic_name: string;
  username: string;
  survey_session_id: string;
  patient_code: string;
  birthday: string;
  gender: string;
  stw_type: string;
  started_time: string;
  update_time: string;
  report: string;
  today: string;
  accordionCurrent: string;
  accordionInformation: string;
  accordionSurvey: string;
  accordionReport: string;
  accPatients: string;
  accClinics: string;
}

interface PatientReportProps {
  title: string;
}

const PatientReport = ({ title }: PatientReportProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const { reportGetSurveySessionReportError } = useAlertHelpers();

  const initialState: PatientReportState = {
    user_type: "",
    clinic_id: "",
    clinic_name: "",
    username: "",
    survey_session_id: "",
    patient_code: "",
    birthday: "",
    gender: "",
    stw_type: "",
    started_time: "",
    update_time: "",
    report: "",
    today: getGermanTodayDisplay(),
    accordionCurrent: "-1",
    accordionInformation: "-1",
    accordionSurvey: "-1",
    accordionReport: "-1",
    accPatients: "-1",
    accClinics: "-1",
  };

  const [state, updateState] = usePartialState(initialState);

  /**Extract scripts in html-response.
   */
  const extractScript = useCallback((reportContent: string) => {
    const scriptMatch = /<script\s*.*>[\s\S]*<\/script>/gi.exec(reportContent);
    if (scriptMatch) {
      const runScript = scriptMatch[0];
      if (runScript !== undefined) {
        const updateScript = runScript.replace(
          /<\/script>([\s\S]*?)<script[^>]*>/gm,
          "",
        );
        const cleanScript = updateScript.replace(
          /<script[^>]*>([\s\S]*?)<\/script>/gm,
          "$1",
        );

        setTimeout(() => {
          // eslint-disable-next-line
          window.eval(cleanScript);
        }, 100);
      }
    }
  }, []);

  /**Method for loading the patient report from backend.
   * The final_report script would be append to the component after loading the report from backend.
   */
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = title;

    // Check if location state exists
    if (!locationState) {
      console.warn("Location state is missing");
      return;
    }

    // Handle patient code
    if (locationState.user_type === "patient" && locationState.username) {
      updateState({ patient_code: locationState.username });
    } else if (locationState.patient_code) {
      updateState({ patient_code: locationState.patient_code });
    }

    const {
      clinic_id = "",
      clinic_name = "",
      username = "",
      survey_session_id = "",
      birthday = "",
      gender = "",
      stw_type = "",
      started_time = "",
      user_type = "",
    } = locationState;

    updateState({
      clinic_id,
      clinic_name,
      username,
      survey_session_id,
      birthday,
      gender,
      stw_type,
      started_time,
      user_type,
    });

    const loadReport = async () => {
      const surveySessionId = locationState.survey_session_id;

      try {
        if (!surveySessionId) throw new Error("Survey session id is required.");
        const reportHtml = await getPatientReport(Number(surveySessionId));
        //response is designed to be empty now,no error thrown
        if (!reportHtml) {
          updateState({ report: "" });
          return;
        }
        updateState({ report: reportHtml });
        appendScript(`${process.env.PUBLIC_URL}/js/final_report.js`);
        extractScript(reportHtml); //solve the async problem of setState
      } catch (error) {
        reportGetSurveySessionReportError(toErrorType(error));
      }
    };

    loadReport();

    //cleanup function
    return () => {
      removeScript(`${process.env.PUBLIC_URL}/js/final_report.js`);
    };
  }, [
    title,
    locationState,
    extractScript,
    updateState,
    reportGetSurveySessionReportError,
  ]);

  return (
    <div className="header-fixed" id="body">
      <div className="skip-link-div">
        <a className="skip-link" href="#final-report">
          Direkt zum Inhalt springen
        </a>
      </div>
      <NavigationBar />
      <div className="page-wrapper-new">
        <div className="wrapper">
          <div className="skip-sidebar-div">
            <a className="skip-sidebar" href="#final-report">
              Navigation überspringen
            </a>
          </div>
          {state.user_type === "patient" ? (
            <SidebarPatient />
          ) : (
            <SidebarClinician />
          )}

          <div className="content-wrapper">
            <div
              className="content ergebnisbericht_container"
              id="final-report"
            >
              <Col id="report-heading" lg="12">
                <h1>Ergebnisbericht</h1>
              </Col>

              <Card
                tabIndex={0}
                id="report-head-info"
                className="card-default card-report-custom widget-media p-4 active border"
              >
                <Card.Body className="m-0 p-0">
                  <Row>
                    <Col className="col-12 col-sm-6 col-print-6">
                      <Row>
                        <Col md="3">
                          <div className="icon rounded-circle-outline ml-2">
                            <span
                              className="material-symbols-rounded"
                              style={{ color: "#1c446a" }}
                            >
                              3p
                            </span>
                          </div>
                        </Col>
                        <Col md="9">
                          <Row className="profile-data profile-data-report">
                            <Col md={5}>SysDok-ID:</Col>
                            <Col md={5} className="text-grey">
                              {state.patient_code}
                            </Col>
                          </Row>
                          <hr className="separator text-grey" />
                          <Row className="profile-data profile-data-report">
                            <Col md={5}>Geburtsdatum:</Col>
                            <Col md={5} className="text-grey">
                              {state.user_type === "patient"
                                ? "******"
                                : state.birthday}
                            </Col>
                          </Row>
                          <hr className="separator text-grey" />
                          <Row className="profile-data profile-data-report">
                            <Col md={5}>Geschlecht:</Col>
                            <Col md={5} className="text-grey">
                              {state.user_type === "patient"
                                ? "******"
                                : state.gender === "MALE"
                                  ? "Männlich"
                                  : state.gender === "FEMALE"
                                    ? "Weiblich"
                                    : state.gender === "DIVERSE"
                                      ? "Divers"
                                      : state.gender}
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                    <Col className="col-12 col-sm-6 col-print-6">
                      <Row>
                        <Col md="3">
                          <div className="icon rounded-circle-outline ml-2">
                            <span
                              className="material-symbols-rounded"
                              style={{ color: "#1c446a" }}
                            >
                              timeline
                            </span>
                          </div>
                        </Col>
                        <Col md="9">
                          <Row className="profile-data profile-data-report">
                            <Col md={5}>Messzeitpunkt:</Col>
                            <Col md={5} className="text-grey">
                              {(() => {
                                switch (state.stw_type) {
                                  case "PRE":
                                    return "Aufnahme";
                                  case "INTERMEDIATE":
                                    return "Zwischenbefragung";
                                  case "POST":
                                    return "Entlassung";
                                  case "FOLLOW_UP_1":
                                    return "Nachsorge 6 Monate";
                                  case "FOLLOW_UP_2":
                                    return "Nachsorge 12 Monate";
                                  case "FOLLOW_UP_3":
                                    return "Nachsorge 18 Monate";
                                  default:
                                    return null;
                                }
                              })()}
                            </Col>
                          </Row>
                          <hr className="separator text-grey" />
                          <Row className="profile-data profile-data-report">
                            <Col md={5}>Screening Datum:</Col>
                            <Col md={5} className="text-grey">
                              {state.started_time ?? "Kein Datum vorhanden"}
                            </Col>
                          </Row>
                          <Row className="profile-data profile-data-report">
                            <Col md={5}>Letztes Update des Screenings:</Col>
                            <Col md={5} className="text-grey">
                              {state.update_time ?? "Kein Datum vorhanden"}
                            </Col>
                          </Row>
                          <hr className="separator text-grey" />
                          <Row className="profile-data profile-data-report">
                            <Col md={5}>Abrufdatum:</Col>
                            <Col md={5} className="text-grey">
                              {state.today}
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Row>
                <Col>
                  <Accordion
                    defaultActiveKey="0"
                    className="card-default accordion"
                    id="report_overview_box"
                  >
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <MdViewList className="mr-1" /> Seitenübersicht
                      </Accordion.Header>
                      <Accordion.Body className="p-0 pt-3 ">
                        <ul
                          className="list-unstyled"
                          id="final_report_overview"
                        >
                          <li>
                            <a
                              href="#dep_patient_container"
                              className="media media-message eb-navigation"
                            >
                              <div className="media-body d-flex">
                                <div
                                  className="position-relative mr-3 list_item_dep_status"
                                  id="list_item_dep_status"
                                ></div>
                                <h4 className="title">
                                  Ihr Ergebnis: Depression
                                </h4>
                              </div>
                            </a>
                          </li>
                          <li>
                            <a
                              href="#anx_patient_container"
                              className="media media-message eb-navigation"
                            >
                              <div className="media-body d-flex">
                                <div
                                  className="position-relative mr-3 list_item_anx_status"
                                  id="list_item_anx_status"
                                ></div>
                                <h4 className="title">Ihr Ergebnis: Angst</h4>
                              </div>
                            </a>
                          </li>

                          <li
                            id="list_item_management_container"
                            style={{ display: "none" }}
                          >
                            <a
                              href="#management_container"
                              className="media media-message eb-navigation"
                            >
                              <div className="media-body d-flex">
                                <h4 className="title">
                                  Erweiterter Gesundheits-Check
                                </h4>
                              </div>
                            </a>
                          </li>
                          <li
                            id="list_item_entlassbericht_container"
                            style={{ display: "none" }}
                          >
                            <a
                              href="#entlassbericht_container"
                              className="media media-message eb-navigation"
                            >
                              <div className="media-body d-flex">
                                <h4 className="title">Entlassbericht</h4>
                              </div>
                            </a>
                          </li>
                        </ul>
                        {state.user_type === "clinician" ? (
                          <div className="card-footer">
                            <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                              <Button
                                variant="primary"
                                onClick={() => navigate(-1)}
                              >
                                <span className="material-symbols-rounded mr-1">
                                  assignment_ind
                                </span>
                                Zurück zum Bericht für Mitarbeitende
                              </Button>
                            </div>
                          </div>
                        ) : null}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Col>
                <Col>
                  <Card
                    className="widget-media p-4 active border card-report-custom"
                    tabIndex={0}
                  >
                    <Card.Title className="d-flex justify-content-between">
                      <h5>Ergebnisbericht drucken</h5>
                    </Card.Title>
                    <Card.Body className="m-0 p-0">
                      <Button variant="primary" id="print_final_report">
                        <MdLocalPrintshop /> Drucken
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              {state.report ? (
                <div
                  id="final-html"
                  dangerouslySetInnerHTML={{ __html: state.report }}
                />
              ) : (
                <Card.Text className="text-muted fst-italic">
                  Berichte sind derzeit nicht verfügbar.
                </Card.Text>
              )}

              {/* END html start */}

              <Row className="mt-3" id="management_container">
                <Col lg="12">
                  <Accordion className="accordion" id="management_clinician">
                    <Accordion.Item eventKey="0">
                      <Accordion.Header id="heading_management_clinician">
                        Erweiterter Gesundheits-Check
                      </Accordion.Header>
                      <Accordion.Body id="management_content_wrapper"></Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Col>
              </Row>

              <Row
                className="entlassbericht mt-3"
                id="entlassbericht_container"
              >
                <Col lg="12">
                  <Accordion
                    id="entlassbericht_clinician"
                    className="accordion"
                  >
                    <Accordion.Item eventKey="0">
                      <Accordion.Header id="heading_entlassbericht_clinician">
                        Entlassbericht
                      </Accordion.Header>
                      <Accordion.Body id="entlassbericht_content_wrapper"></Accordion.Body>
                      <div className="card-footer">
                        <div className="d-grip gap-2 d-md-flex justify-content-md-end">
                          <Button variant="primary" id="print_entlassbericht">
                            <MdLocalPrintshop /> Entlassbericht drucken
                          </Button>
                          <Button
                            variant="outline-secondary"
                            id="copy_entlassbericht"
                          >
                            In Zwischenablage kopieren
                          </Button>
                        </div>
                      </div>
                    </Accordion.Item>
                  </Accordion>
                </Col>
              </Row>
              {/* END html end*/}
            </div>
          </div>
          <Footer>
            {state.clinic_id}
            {state.clinic_name}
            {state.username}
            {state.user_type}
          </Footer>
        </div>
      </div>
    </div>
  );
};

export default PatientReport;
